import React, { Fragment, memo, useEffect } from 'react'
import { differenceInDays, getDaysInMonth } from 'date-fns'

import { Popup } from '@/ui'
import {
  getCalendarEventsForDate,
  setInitialPlacementIndices,
  updatePlacementIndices,
} from '@/ui/MultiViewCalendar/MonthView/helpers'
import ShowMorePopup from '@/ui/MultiViewCalendar/MonthView/ShowMorePopup'
import format from '@/helpers/format'
import { CalendarEntry } from './Styles'
import { CalendarEventWithPlacementIndices } from './'
import useModalStore from '@/stores/useModalStore'
import VacationCalendarContext from '../../../components/VacationCalendar/VacationCalendarContext'
import { useContext } from 'react'

type CalendarTileProps = {
  date: Date
  view: string
  showNeighboringMonths: boolean
  generationTime: Date
  calendarEvents: CalendarEventWithPlacementIndices[]
}

const CalendarTile = ({
  date,
  view,
  showNeighboringMonths,
  calendarEvents,
}: CalendarTileProps) => {
  const { openPopupId, setOpenPopupId } = useContext(VacationCalendarContext)

  const modals = useModalStore(state => state.modals)

  useEffect(() => {
    setOpenPopupId(null)
  }, [modals.length])

  if (view !== 'month') {
    return null
  }

  const formattedDate = format(date, 'yyyy-MM-dd') || ''
  const dayNumberOfWeek = (date.getDay() + 6) % 7 // remap so monday is index 0, instead of sunday
  const dayOfWeek = format(date, 'EEE')
  const dayOfMonth = parseInt(format(date, 'd') || '', 10)
  const numberOfDaysInMonth = getDaysInMonth(date)
  const maxEntryLength = showNeighboringMonths
    ? 7 - dayNumberOfWeek
    : Math.min(7 - dayNumberOfWeek, numberOfDaysInMonth - dayOfMonth + 1)

  setInitialPlacementIndices(date, calendarEvents)
  updatePlacementIndices(date, calendarEvents)

  const calendarEventsForDate = getCalendarEventsForDate(date, calendarEvents)

  const displayedEvents = calendarEventsForDate.filter(
    event => event.placementIndex < 3,
  )

  return (
    <Fragment>
      {displayedEvents.map(event => {
        let className = 'vacation-period'

        const remainingDuration = differenceInDays(event.endDate, date) + 1

        const vacationEndsThisWeek = dayNumberOfWeek + remainingDuration <= 7

        if (
          formattedDate === event.formattedStartDate &&
          (formattedDate === event.formattedEndDate || vacationEndsThisWeek)
        ) {
          className = 'vacation-period-start-end'
        } else if (formattedDate === event.formattedStartDate) {
          className = 'vacation-period-start'
        } else if (vacationEndsThisWeek) {
          className = 'vacation-period-end'
        }

        const hasChangedPlacementIndex =
          Object.keys(event?.placementIndices ?? {}).length > 1 &&
          Object.keys(event?.placementIndices ?? {}).includes(formattedDate)

        if (hasChangedPlacementIndex) {
          className = `${className} pointy-start`
        }

        if (!event.allDay) {
          className = `${className} not-all-day`
        }

        if (!event.hasBeenApproved) {
          className = `${className} unapproved`
        }

        const shouldRender =
          formattedDate === event.formattedStartDate ||
          dayNumberOfWeek === 0 ||
          (!showNeighboringMonths && dayOfMonth === 1) ||
          hasChangedPlacementIndex

        if (!shouldRender) {
          return null
        }

        return (
          <Popup
            key={event.id}
            content={event.popupContent}
            on="click"
            open={openPopupId === `${event.id}-${formattedDate}`}
            onOpen={() => setOpenPopupId(`${event.id}-${formattedDate}`)}
            onClose={() => setOpenPopupId(null)}
            inverted
            position="top center"
            flowing
            disabled={!event.popupContent}
            style={{ borderRadius: '10px' }}
            trigger={
              <CalendarEntry
                style={{ top: `${event.placementIndex * 26 + 46}px` }}
                length={Math.min(remainingDuration, maxEntryLength)}
                color={event.color}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                className={className}>
                {(shouldRender || dayOfWeek === 'Mon') && (
                  <span
                    className="vacation-name"
                    title={event.title}
                    style={
                      event.hasBeenDenied
                        ? {
                            textDecoration: 'line-through 2px solid',
                          }
                        : {}
                    }>
                    {event.label}
                  </span>
                )}
              </CalendarEntry>
            }
          />
        )
      })}
      {calendarEventsForDate.length > displayedEvents.length && (
        <ShowMorePopup
          formattedDate={formattedDate}
          calendarEvents={calendarEventsForDate}
          numberOfUndisplayedEvents={
            calendarEventsForDate.length - displayedEvents.length
          }
        />
      )}
    </Fragment>
  )
}

export default memo(CalendarTile, (prevProps, nextProps) => {
  if (prevProps.showNeighboringMonths !== nextProps.showNeighboringMonths) {
    return false
  }

  if (prevProps.generationTime !== nextProps.generationTime) {
    return false
  }

  if (
    prevProps.calendarEvents.length === 0 &&
    nextProps.calendarEvents.length === 0
  ) {
    return false
  }

  return true
})
